import { useEffect, useMemo, useState } from 'react';
import {
  Text, Badge, Spinner, Button, Textarea, TabList, Tab, SearchBox,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular, Edit20Regular, Delete20Regular } from '@fluentui/react-icons';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useT } from '../../i18n';
import { useEntityNames } from '../../hooks/useEntityNames';
import { useLocaleSettings } from '../../hooks/useLocaleSettings';
import { usePermission } from '../../hooks/usePermission';
import { useLastSeenActivity } from '../../hooks/useLastSeenActivity';
import { useAuthStore } from '../../store/auth.store';
import { searchActivities, updateActivityNotes, deleteActivity } from '../../api/lotes.api';
import type { Activity, ActivityType } from '../../types/api.types';

// Color por tipo de actividad (nunca cambia — no es texto, no necesita traducción)
const TYPE_COLOR: Record<ActivityType, 'brand' | 'success' | 'warning' | 'danger' | 'informative' | 'important'> = {
  PREPARACION:   'informative',
  SIEMBRA:       'success',
  RIEGO:         'brand',
  FERTILIZACION: 'success',
  FITOSANITARIO: 'warning',
  COSECHA:       'success',
  MAQUINARIA:    'informative',
  LABORES:       'informative',
  OTRO:          'informative',
  // NOVEDAD es una observación de campo, no una labor con costo — color propio
  // ("important", violeta en el tema de FluentUI) para que no se confunda con
  // el resto de las operaciones en el feed.
  NOVEDAD:       'important',
  CLIMA:         'brand',
};

const useStyles = makeStyles({
  list: { display: 'flex', flexDirection: 'column', gap: '8px' },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  row: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '10px 16px',
    gap: '12px',
    cursor: 'pointer',
  },
  left: { display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 },
  cost: { fontWeight: '600', whiteSpace: 'nowrap' },
  details: {
    padding: '0 16px 12px 16px',
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  toolbar: { display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' },
  notesRow: { display: 'flex', flexDirection: 'column', gap: '6px' },
  notesActions: { display: 'flex', gap: '8px' },
  stockChildren: {
    display: 'flex', flexDirection: 'column', gap: '2px',
    margin: '0 16px 8px 40px',
    paddingLeft: '10px',
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
  },
  stockChild: { color: tokens.colorNeutralForeground2 },
});

interface Props {
  activities: Activity[];
  isLoading?: boolean;
  /** Cuando se pasa, la búsqueda queda acotada a este lote (LoteDetailPage). Omitido = cross-lote. */
  loteId?: string;
}

/**
 * Construye una frase en lenguaje natural a partir de los campos crudos de
 * una actividad — pura y determinista (sin LLM), a diferencia del
 * `resumen_confirmacion` que arma el bot de campo al confirmar un draft.
 */
function buildActivityNarrative(a: Activity, t: ReturnType<typeof useT>, loteName: (id: string) => string): string {
  const person = a.recordedByName ?? t.activityFeed.unknownPerson;
  const verb = (t.activityFeed.verb as Record<string, string>)[a.type] ?? t.activityFeed.verb.OTRO;
  const details = a.materials.length
    ? a.materials.map(m => `${m.name} ${m.quantity}${m.unit}`).join(', ')
    : '';
  const date = a.occurredAt.slice(0, 10);

  return `${person} ${verb}${details ? `: ${details}` : ''} — ${loteName(a.loteId)} · ${date}`;
}

function ActivityCard({ activity, t, formatCurrency }: {
  activity: Activity;
  t: ReturnType<typeof useT>;
  formatCurrency: (n: number) => string;
}) {
  const { loteName } = useEntityNames();
  const s = useStyles();
  const qc = useQueryClient();
  const canEdit = usePermission('lotes:write');
  const canApprove = usePermission('lotes:approve');
  const myPhone = useAuthStore(st => st.user?.phoneNumber);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Misma regla que el servidor (decisión 5): el autor, dentro de las 24 h de haberlo creado; después, quien aprueba.
  const createdMs = activity.createdAt ? Date.parse(activity.createdAt) : NaN;
  const authorInWindow = !!myPhone && activity.recordedBy === myPhone
    && Number.isFinite(createdMs) && Date.now() - createdMs <= 24 * 60 * 60 * 1000;
  const canDelete = canEdit && (canApprove || authorInWindow);
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftNotes, setDraftNotes] = useState(activity.notes ?? '');

  const saveMut = useMutation({
    mutationFn: (notes: string) => updateActivityNotes(activity.loteId, activity.activityId, notes),
    onSuccess: () => {
      setEditing(false);
      qc.invalidateQueries({ queryKey: ['loteActivities', activity.loteId] });
      qc.invalidateQueries({ queryKey: ['recentActivities'] });
      qc.invalidateQueries({ queryKey: ['activitiesSearch'] });
    },
  });

  const deleteMut = useMutation({
    mutationFn: () => deleteActivity(activity.loteId, activity.activityId),
    onSuccess: () => {
      setConfirmDelete(false);
      qc.invalidateQueries({ queryKey: ['activitiesSearch'] });
      qc.invalidateQueries({ queryKey: ['loteActivities', activity.loteId] });
      qc.invalidateQueries({ queryKey: ['recentActivities'] });
    },
  });

  return (
    <div className={s.card}>
      <div className={s.row} onClick={() => setExpanded(v => !v)}>
        <div className={s.left}>
          {expanded ? <ChevronDown20Regular /> : <ChevronRight20Regular />}
          <Badge color={TYPE_COLOR[activity.type] ?? 'informative'} appearance="tint">
            {t.activityTypes[activity.type] ?? activity.type}
          </Badge>
          <Text size={200} truncate wrap={false}>{buildActivityNarrative(activity, t, loteName)}</Text>
        </div>
        <span className={s.cost}>{formatCurrency(activity.totalCost)}</span>
      </div>

      {activity.stockMovements && activity.stockMovements.length > 0 && (
        <div className={s.stockChildren}>
          {activity.stockMovements.map((m, i) => (
            <Text key={`${m.productName}-${i}`} size={200} className={s.stockChild}>
              {t.activityFeed.stockUsed
                .replace('{product}', m.productName)
                .replace('{qty}', String(m.quantity))
                .replace('{unit}', m.unit)}
            </Text>
          ))}
        </div>
      )}

      {expanded && (
        <div className={s.details} onClick={(e) => e.stopPropagation()}>
          {activity.materials.length > 0 && (
            <Text size={200}>
              <strong>{t.activityFeed.materialsLabel}:</strong>{' '}
              {activity.materials.map(m => `${m.name} ${m.quantity}${m.unit}`).join(', ')}
            </Text>
          )}

          {activity.type === 'SIEMBRA' && activity.sowingData && (
            <>
              {activity.sowingData.plantingDensity != null && (
                <Text size={200}>
                  <strong>{t.activityFeed.sowingDensity}:</strong>{' '}
                  {activity.sowingData.plantingDensity}{activity.sowingData.plantingDensityUnit ? ` ${activity.sowingData.plantingDensityUnit}` : ''}
                </Text>
              )}
              {activity.sowingData.rowSpacingCm != null && (
                <Text size={200}><strong>{t.activityFeed.sowingRowSpacing}:</strong> {activity.sowingData.rowSpacingCm} cm</Text>
              )}
              {activity.sowingData.plantSpacingCm != null && (
                <Text size={200}><strong>{t.activityFeed.sowingPlantSpacing}:</strong> {activity.sowingData.plantSpacingCm} cm</Text>
              )}
              {activity.sowingData.genetics && (
                <Text size={200}><strong>{t.activityFeed.sowingGenetics}:</strong> {activity.sowingData.genetics}</Text>
              )}
            </>
          )}

          {activity.type === 'CLIMA' && activity.weatherData && (
            <Text size={200}>
              <strong>{activity.weatherData.eventKind === 'HELADA' ? t.activityFeed.weatherFrost : t.activityFeed.weatherRain}:</strong>{' '}
              {[
                activity.weatherData.mm != null ? `${activity.weatherData.mm} mm` : null,
                activity.weatherData.minTempC != null ? `${activity.weatherData.minTempC} °C` : null,
              ].filter(Boolean).join(' · ')}
            </Text>
          )}

          {editing ? (
            <div className={s.notesRow}>
              <Textarea
                value={draftNotes}
                onChange={(_, d) => setDraftNotes(d.value)}
                placeholder={t.activityFeed.notesPlaceholder}
                resize="vertical"
              />
              {saveMut.isError && <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>{t.activityFeed.saveError}</Text>}
              <div className={s.notesActions}>
                <Button size="small" appearance="primary" disabled={saveMut.isPending} onClick={() => saveMut.mutate(draftNotes)}>
                  {t.activityFeed.saveNotes}
                </Button>
                <Button size="small" disabled={saveMut.isPending} onClick={() => { setEditing(false); setDraftNotes(activity.notes ?? ''); }}>
                  {t.activityFeed.cancelEdit}
                </Button>
              </div>
            </div>
          ) : (
            <div className={s.notesRow}>
              <Text size={200}>
                <strong>{t.activityFeed.notesLabel}:</strong> {activity.notes || '—'}
              </Text>
              {canEdit && (
                <Button
                  size="small"
                  icon={<Edit20Regular />}
                  onClick={() => { setDraftNotes(activity.notes ?? ''); setEditing(true); }}
                >
                  {t.activityFeed.editNotes}
                </Button>
              )}
            </div>
          )}

          {canDelete && (
            <div className={s.notesActions}>
              <Button
                size="small"
                icon={<Delete20Regular />}
                onClick={() => { deleteMut.reset(); setConfirmDelete(true); }}
              >
                {t.activityFeed.delete}
              </Button>
            </div>
          )}

          <Dialog open={confirmDelete} onOpenChange={(_, d) => { if (!deleteMut.isPending) setConfirmDelete(d.open); }}>
            <DialogSurface>
              <DialogBody>
                <DialogTitle>{t.activityFeed.deleteConfirmTitle}</DialogTitle>
                <DialogContent>
                  {/* El nombre va como texto de React, nunca como HTML. */}
                  <Text>{t.activityFeed.deleteConfirmBody.replace('{name}', loteName(activity.loteId))}</Text>
                  {deleteMut.isError && (
                    <Text size={200} block style={{ color: tokens.colorPaletteRedForeground1 }}>{t.activityFeed.deleteError}</Text>
                  )}
                </DialogContent>
                <DialogActions>
                  <Button disabled={deleteMut.isPending} onClick={() => setConfirmDelete(false)}>
                    {t.activityFeed.cancelEdit}
                  </Button>
                  <Button appearance="primary" disabled={deleteMut.isPending} onClick={() => deleteMut.mutate()}>
                    {t.activityFeed.delete}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      )}
    </div>
  );
}

export function ActivityFeed({ activities, isLoading, loteId }: Props) {
  const s = useStyles();
  const t = useT();
  const { formatCurrency } = useLocaleSettings();
  const [activeType, setActiveType] = useState<ActivityType | 'ALL' | 'MINE'>('ALL');
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [onlyNew, setOnlyNew] = useState(false);
  const user = useAuthStore(st => st.user);
  const { lastSeenAt, markSeen } = useLastSeenActivity(user?.tenantId ?? '', user?.phoneNumber ?? '');

  // Se marca como visto al salir del feed, no al entrar (si no, el contador nunca se vería).
  useEffect(() => markSeen, [markSeen]);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(id);
  }, [query]);

  const mineOnly = activeType === 'MINE';
  // "Mis datos" siempre consulta al servidor (la lista cargada no distingue autor), aunque no haya texto.
  const newFilter = onlyNew && !!lastSeenAt;
  const searching = debouncedQuery.length > 0 || mineOnly || newFilter;

  const searchQuery = useQuery({
    queryKey: ['activitiesSearch', debouncedQuery, activeType, loteId, newFilter ? lastSeenAt : null],
    queryFn: () => searchActivities({
      q: debouncedQuery || undefined,
      type: activeType === 'ALL' || activeType === 'MINE' ? undefined : activeType,
      mine: mineOnly || undefined,
      createdAfter: newFilter ? lastSeenAt! : undefined,
      loteId,
    }),
    enabled: searching,
  });

  const typesPresent = useMemo(() => {
    const set = new Set<ActivityType>();
    activities.forEach(a => set.add(a.type));
    return Array.from(set);
  }, [activities]);

  // Datos nuevos: grabados por otros después de la última visita (sin createdAt no cuentan).
  const newCount = useMemo(() => {
    if (!lastSeenAt) return 0;
    return activities.filter(a => !!a.createdAt && a.createdAt > lastSeenAt && a.recordedBy !== user?.phoneNumber).length;
  }, [activities, lastSeenAt, user?.phoneNumber]);

  const displayed = searching
    ? (searchQuery.data?.items ?? [])
    : activities.filter(a => activeType === 'ALL' || a.type === activeType).slice(0, 10);

  const loading = searching ? searchQuery.isLoading : isLoading;

  return (
    <div>
      <div className={s.toolbar}>
        <TabList
          selectedValue={activeType}
          onTabSelect={(_, d) => setActiveType(d.value as ActivityType | 'ALL' | 'MINE')}
          size="small"
        >
          <Tab value="ALL">{t.activityFeed.allTab}</Tab>
          <Tab value="MINE">{t.activityFeed.mineTab}</Tab>
          {typesPresent.map(type => (
            <Tab key={type} value={type}>{t.activityTypes[type] ?? type}</Tab>
          ))}
        </TabList>
        {newCount > 0 && (
          <Button
            size="small"
            appearance={onlyNew ? 'primary' : 'secondary'}
            aria-pressed={onlyNew}
            onClick={() => setOnlyNew(v => !v)}
          >
            {t.activityFeed.newBadge.replace('{count}', String(newCount))}
          </Button>
        )}
        <SearchBox
          placeholder={t.activityFeed.searchPlaceholder}
          value={query}
          onChange={(_, d) => setQuery(d.value)}
          style={{ minWidth: '220px' }}
        />
      </div>

      {loading ? (
        <Spinner size="small" label={t.activityFeed.loading} />
      ) : displayed.length === 0 ? (
        <Text>{searching ? t.activityFeed.noResults : t.activityFeed.empty}</Text>
      ) : (
        <div className={s.list}>
          {displayed.map((a) => (
            <ActivityCard key={a.activityId} activity={a} t={t} formatCurrency={formatCurrency} />
          ))}
        </div>
      )}
    </div>
  );
}
